import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { useState, useEffect, useRef } from 'react';

const DEFAULT_REQUEST_TIMEOUT_MS = 20000;

/**
 * Emit a request with requestId and wait for a single 'response' event (with timeout).
 * Use for events where the server supports requestId and emits 'response' with { requestId, error?, data? }.
 * Falls back to callback-style if server doesn't send requestId response (caller can use callback path separately).
 */
export function socketRequest<T = any>(
    socket: any,
    eventName: string,
    payload: Record<string, unknown>,
    options: { timeoutMs?: number } = {}
): Promise<T> {
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

    if (!socket?.connected) {
        return Promise.reject(new Error(`Socket not connected: ${eventName}`));
    }

    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            socket.off('response', handler);
            reject(new Error(`Request timeout: ${eventName} (${timeoutMs}ms)`));
        }, timeoutMs);

        const handler = (res: { requestId?: string; error?: any; data?: T }) => {
            if (res.requestId !== requestId) return;
            clearTimeout(timer);
            socket.off('response', handler);
            if (res.error != null) {
                reject(typeof res.error === 'object' && res.error?.message ? res.error : new Error(String(res.error)));
            } else {
                resolve(res.data as T);
            }
        };

        socket.on('response', handler);
        socket.emit(eventName, { ...payload, requestId });
    });
}

function useAuthenticatedSocket() {
    const userStr = localStorage.getItem('user');
    const _user = userStr ? JSON.parse(userStr) : null;
    const baseUrl = process.env.REACT_APP_SOCKET_URL;
    const url = baseUrl ? `${baseUrl}/clients` : '';

    const { socket, connected, error } = useSocket(url, {
        auth: { token: _user?.token ?? '' },
        transports: ['websocket'],
    });

    return {
        authSocket: socket,
        connected,
        error,
    };
}


/* Devices */

function useDevices() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'devices');
    const hasFetched = useRef(false);

    // Fetch devices when socket connects (handles page reload)
    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getDevices');
        }
    }, [connected, authSocket]);

    // Reset fetch flag when disconnected
    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const getDevicesByDeviceType = (id) => {
        if (id != 0) {
            var devices = lastMessage && lastMessage.filter((device: any) => device.deviceTypes.id == id);
            return { devices: devices };
        }
        else {
            return { devices: lastMessage };
        }
    }

    const getDeviceByDeviceType = (deviceTypeId) => {
        return socketRequest(socket, 'getDevicebyDeviceType', { deviceTypeId });
    }

    return { socket, devices: lastMessage, getDevicesByDeviceType, getDeviceByDeviceType, connected }
}

function useDevice() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'device');
    const { devices } = useDevices();

    const getDevice = (id) => {
        var device = devices && devices.find((device: any) => device.id == id)
        return { device: device };
    }
    const addDevice = (device) => {
        console.log('addDevice: emitting request', device);
        socket.emit('addDevice', { device }, (response) => {
            console.log('addDevice: received response', response);
            // Refresh device groups to update the list
            socket.emit('getDeviceGroups');
        });
    }

    const updateDevice = (deviceId: number, device: { name: string }) => {
        return new Promise((resolve, reject) => {
            socket.emit('updateDevice', { deviceId, device }, (response: any) => {
                if (response?.status && response.status !== 200) {
                    reject(response.message);
                } else {
                    resolve(response);
                }
            });
        });
    }

    const deleteDevice = (deviceId) => {
        return new Promise((resolve, reject) => {
            socket.emit('deleteDevice',
                {
                    deviceId
                }, (response) => {
                    // Refresh device groups to update the list
                    socket.emit('getDeviceGroups');
                    resolve(response);
                })
        })
    }

    const setDeviceSettings = (deviceId: number, settings: any) => {
        socket.emit('updateDeviceSettings', { deviceId, settings });
    }

    const getAvailablePorts = (deviceId) => {
        console.log("hostDveiceId", deviceId)
        return new Promise((resolve, reject) => {
            socket.emit("getAvailablePorts", {
                deviceId
            }, (response) => {
                console.log("response", response)
                resolve(response)
            }
            )
        })
    }

    const cameraCapture = (deviceId, datasetId, name?) => {

        return new Promise((resolve, reject) => {
            socket.emit('cameraCapture', {
                deviceId,
                datasetId,
                name
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const confirmCapture = (processedImageData, originalImageData, metrics, deviceId, datasetId, name?) => {
        return new Promise((resolve, reject) => {
            socket.emit('confirmCapture', {
                processedImageData,
                originalImageData,
                metrics,
                deviceId,
                datasetId,
                name
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const stereoCapture = (deviceId, datasetId, name?) => {
        return new Promise((resolve, reject) => {
            socket.emit('stereoCapture', {
                deviceId,
                datasetId,
                name
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const confirmStereoCapture = (scenePly, objectPly, metrics, deviceId, datasetId, name?) => {
        return new Promise((resolve, reject) => {
            socket.emit('confirmStereoCapture', {
                scenePly,
                objectPly,
                metrics,
                deviceId,
                datasetId,
                name
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    /**
     * Progressively stream the full-resolution isolated object as ordered binary
     * chunks. onChunk receives ({ data: ArrayBuffer, chunkPoints, index, totalChunks,
     * totalPoints }); onDone/onError fire at the end. Returns a cleanup fn.
     */
    const streamStereoObject = (
        deviceId,
        handlers: {
            onChunk?: (c: any) => void;
            onDone?: (info: any) => void;
            onError?: (err: string) => void;
        }
    ) => {
        // NOTE: server broadcasts (NotifyGroup) arrive on authSocket, NOT the
        // per-event `socket` wrapper — subscribe there or chunks never fire.
        const sock = authSocket;
        if (!sock) {
            handlers.onError?.('socket_not_ready');
            return () => {};
        }
        const listener = (evt: any) => {
            // Ignore chunks for other devices (broadcast to the org group).
            if (evt && evt.deviceId != null && String(evt.deviceId) !== String(deviceId)) return;
            if (evt?.done) {
                if (evt.error) handlers.onError?.(evt.error);
                else handlers.onDone?.(evt);
                sock.off('stereoObjectChunk', listener);
                return;
            }
            handlers.onChunk?.(evt);
        };
        sock.on('stereoObjectChunk', listener);
        sock.emit('stereoStreamObject', { deviceId });
        // Cleanup: detach the listener (e.g. if the modal closes mid-stream).
        return () => sock.off('stereoObjectChunk', listener);
    };

    /**
     * Run segmentation diagnostics: returns { ok, images: {color,depth,overlay,
     * object} (base64 jpg), stats } so the Diagnostics tab can show what the
     * object isolation actually selected.
     */
    const stereoDiagnose = (deviceId) => {
        return new Promise((resolve) => {
            socket.emit('stereoDiagnose', { deviceId }, (response: any) => {
                resolve(response);
            });
        });
    };

    /**
     * Build a textured triangle mesh of the isolated object (solid 3D-model look).
     * method: 'depth' (crisp grid) | 'smooth' (Taubin-smoothed). Resolves with
     * { ok, mesh: ArrayBuffer (.ply with faces+colors), vertices, faces, method }.
     */
    const stereoMeshObject = (deviceId, method: 'depth' | 'smooth' = 'depth') => {
        return new Promise((resolve) => {
            socket.emit('stereoMeshObject', { deviceId, method }, (response: any) => {
                resolve(response);
            });
        });
    };

    const cameraCalibrate = (deviceId, ux, uy, saveParameters) => {
        socket.emit('calibrate', {
            deviceId,
            ux,
            uy,
            saveParameters,
            script: 'stream_chess.py'
        });
    }

    const cancelCalibration = (deviceId) => {
        socket.emit('cancelCalibration', { deviceId });
    }

    const rotateStream = (deviceId, direction) => {
        socket.emit('rotateStream', { direction: direction, deviceId: deviceId });
    }

    const getDeviceMetrics = (deviceId: number, organizationId: number, datasetId?: number, from?: Date, to?: Date) => {
        return socketRequest<{ createdAt: string; metrics: any }[]>(authSocket, 'getDeviceMetrics', {
            deviceId,
            organizationId,
            datasetId,
            from: from?.toISOString(),
            to: to?.toISOString(),
        });
    };

    const updateDeviceSettings = (deviceId: number, settings: any) => {
        return new Promise((resolve, reject) => {
            socket.emit('updateDeviceSettings', { deviceId, settings }, (response: any) => {
                if (response?.error) {
                    reject(response.error);
                } else {
                    resolve(response);
                }
            });
        });
    };

    const scanNetwork = (deviceId: number) => {
        return new Promise<any[]>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                reject(new Error('Network scan timed out'));
            }, 120000); // 2 minute timeout for network scan

            socket.emit('scanNetwork', { deviceId }, (response: any) => {
                clearTimeout(timeoutId);
                if (response?.error) {
                    reject(response.error);
                } else {
                    resolve(response?.data || response || []);
                }
            });
        });
    };

    /**
     * Send recipe values to a mixer PLC device. Hive forwards this to the
     * device's Nexus process, which spawns a one-shot Python writer script
     * (Settings.recipeScript) and returns { success, error, log } once done.
     */
    const sendRecipe = (deviceId: number, recipeValues: Record<string, number>, recipeName?: string) => {
        return new Promise<{ success: boolean; error?: string | null; log?: string[] }>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                reject(new Error('Send recipe timed out'));
            }, 95000); // slightly longer than Nexus's own 90s safety timeout

            socket.emit('sendRecipe', { deviceId, recipeValues, recipeName }, (response: any) => {
                clearTimeout(timeoutId);
                resolve(response || { success: false, error: 'No response received' });
            });
        });
    };

    return { socket, device: lastMessage, connected, rotateStream, getDevice, addDevice, updateDevice, deleteDevice, cameraCapture, confirmCapture, stereoCapture, confirmStereoCapture, streamStereoObject, stereoDiagnose, stereoMeshObject, cameraCalibrate, cancelCalibration, setDeviceSettings, getAvailablePorts, getDeviceMetrics, updateDeviceSettings, scanNetwork, sendRecipe };

}



/* Device groups */

function useDeviceGroups() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'deviceGroups');
    const hasFetched = useRef(false);

    // Fetch device groups when socket connects (handles page reload)
    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getDeviceGroups');
        }
    }, [connected, authSocket]);

    // Reset fetch flag when disconnected
    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const fetchDeviceGroups = () => {
        socket.emit('getDeviceGroups');
    }

    const searchDeviceGroups = (searchName) => {
        if (!socket?.connected) return Promise.reject(new Error('Socket not connected'));
        return new Promise((resolve, reject) => {
            socket.emit("getDeviceGroups", { searchName }, (response: any) => resolve(response));
        });
    }

    return { socket, deviceGroups: lastMessage, fetchDeviceGroups, searchDeviceGroups, connected }
}

function useDeviceGroup() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'deviceGroup');
    const { deviceGroups } = useDeviceGroups();

    const getDeviceGroup = (id) => {
        var deviceGroup = deviceGroups && deviceGroups.find((deviceGroup: any) => deviceGroup.id == id)
        return { deviceGroup: deviceGroup };
    }
    const addDeviceGroup = async (deviceGroup) => {
        return new Promise((resolve, reject) => {
            socket.emit('addDeviceGroup', { deviceGroup },
                (response: any) => {
                    resolve(response)
                }
            );
        })
    }

    const updateDeviceGroup = (deviceGroup) => {
        socket.emit('updateDeviceGroup', { deviceGroup });
    }
    const deleteDeviceGroup = (deviceGroupId) => {
        return new Promise((resolve, reject) => {
            socket.emit('deleteDeviceGroup',
                {
                    deviceGroupId
                }, (response) => {
                    if (response.status !== 200) {
                        reject(response.message)
                    } else {
                        resolve(response.message)
                    }
                })
        })
    }
    // Handle Turn on/Turn off 
    const turnOffDeviceGroup = (deviceGroupId) => {
        socket.emit("turnOffDeviceGroup", { deviceGroupId })
    }
    const turnOnDeviceGroup = (deviceGroupId) => {
        socket.emit("turnOnDeviceGroup", { deviceGroupId })
    }
    // Handle Restart Device Group (restarts nexus service)
    const restartDeviceGroup = (deviceGroupId) => {
        socket.emit("restartDeviceGroup", { deviceGroupId })
    }
    // Handle Shutdown Device Group (shuts down host OS)
    const shutdownDeviceGroup = (deviceGroupId) => {
        socket.emit("shutdownDeviceGroup", { deviceGroupId })
    }
    // Handle Reboot Device Group (reboots host OS)
    const rebootDeviceGroup = (deviceGroupId) => {
        socket.emit("rebootDeviceGroup", { deviceGroupId })
    }
    // Handle Start/Pause Device Group
    const pauseDeviceGroup = (deviceGroup) => {
        socket.emit("pauseDeviceGroup", { deviceGroup })
    }
    const startDeviceGroup = (deviceGroup) => {
        socket.emit("startDeviceGroup", { deviceGroup })
    }

    return { socket, deviceGroup: lastMessage, getDeviceGroup, addDeviceGroup, updateDeviceGroup, deleteDeviceGroup, turnOffDeviceGroup, turnOnDeviceGroup, restartDeviceGroup, shutdownDeviceGroup, rebootDeviceGroup, pauseDeviceGroup, startDeviceGroup };

}

// /* Device types */

function useDeviceTypes() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'deviceTypes');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getDeviceTypes');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const fetchDeviceTypes = (searchName) => {
        return new Promise((resolve, reject) => {
            socket.emit("getDeviceTypes",
                { searchName },
                (response: any) => {
                    resolve(response)
                })
        })
    }
    return { socket, deviceTypes: lastMessage, fetchDeviceTypes, connected }
}

function useDeviceType() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'deviceGroup');
    const { deviceGroups } = useDeviceGroups();

    const getDeviceGroup = (id) => {
        var deviceGroup = deviceGroups && deviceGroups.find((deviceGroup: any) => deviceGroup.id == id)
        return { deviceGroup: deviceGroup };
    }
    const addDeviceGroup = (deviceGroup) => {
        socket.emit('addDevice', { deviceGroup });
    }

    const updateDeviceGroup = (deviceGroup) => {
        socket.emit('updateDevice', { deviceGroup });
    }

    const deleteDeviceGroup = (deviceGroup) => {
        return new Promise((resolve, reject) => {
            socket.emit('deleteDevice',
                {
                    deviceGroup
                }, (response) => {
                    resolve(response)
                    // if(response.status !== 200) {
                    //     reject(response.message)
                    // } else {
                    //     resolve(response.message)
                    // }
                })
        })
    }
    return { socket, deviceGroup: lastMessage, getDeviceGroup, addDeviceGroup, updateDeviceGroup, deleteDeviceGroup };

}

/* Metrics */

function useMetrics() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'metrics');
    return { socket, metrics: lastMessage }
}


/* Programs */

function usePrograms() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'programs');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getPrograms');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const searchPrograms = (searchName) => {
        return new Promise((resolve, reject) => {
            socket.emit("getPrograms", {
                searchName,
            }, (response) => {
                resolve(response)
            })
        })
    }
    return { socket, programs: lastMessage, searchPrograms, connected }
}

function useProgram() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'program');
    const { programs } = usePrograms();

    const getProgram = (id) => {
        var program = programs && programs.find((program: any) => program.id == id)
        return { program: program };
    }

    const getProgramsByDeviceGroup = (deviceGroupId) => {
        return socketRequest(socket, 'getProgramsByDeviceGroup', { deviceGroupId });
    }

    const addProgram = async (program) => {
        return new Promise((resolve, reject) => {
            socket.emit('addProgram', {
                program
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const runProgram = (program) => {
        socket.emit('runProgram', { program });
    }

    const updateProgram = (program) => {
        return new Promise((resolve, reject) => {
            socket.emit('updateProgram',
                {
                    program
                }, (response) => {
                    resolve(response)
                })
        })

    }

    const deleteProgram = (programId) => {
        return new Promise((resolve, reject) => {
            socket.emit('deleteProgram',
                {
                    programId
                }, (response) => {
                    if (response.status !== 200) {
                        reject(response.message)
                    } else {
                        resolve(response.message)
                    }
                })
        })
    }

    const stopProgram = (program) => {
        socket.emit('stopProgram', program);
    }
    return { socket, program: lastMessage, getProgram, addProgram, runProgram, updateProgram, deleteProgram, stopProgram, getProgramsByDeviceGroup, connected };
}

function useProgramDone() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'programDone');

    return { socket, programDone: lastMessage }
}

/* Blocks */

function useActiveBlock() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'activeBlock');
    return { socket, activeBlock: lastMessage }
}


/* Actions */

function useActions() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'actions');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getActions');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const searchActions = (searchName) => {
        if (!socket?.connected) return Promise.reject(new Error('Socket not connected'));
        return new Promise((resolve, reject) => {
            socket.emit("getActions", { searchName }, (response: any) => resolve(response));
        });
    }

    return { socket, actions: lastMessage, searchActions, connected }
}

function useAction() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'action');
    const { actions } = useActions();

    const addAction = async (action) => {
        return new Promise((resolve, reject) => {
            socket.emit("addAction", {
                action
            }, (response: any) => {
                resolve(response)
            })
        })
    }

    const deleteAction = (actionId) => {
        return new Promise((resolve, reject) => {
            socket.emit("deleteAction",
                {
                    actionId
                }, (response: any) => {
                    resolve(response)
                })
        })
    };

    const getAction = (actionId) => {
        return new Promise((resolve, reject) => {
            socket.emit('getAction', {
                actionId,
            }, (response) => {
                resolve(response);
            });
        })
    }

    const runAction = (action, deviceId) => {
        socket.emit('runAction', { action: action, deviceId: deviceId });
    }

    const updateAction = (action) => {
        socket.emit('updateAction', { action });
    }

    const stopAction = (action, deviceId) => {
        socket.emit('stopAction', { action: action, deviceId: deviceId });
    }


    return { socket, action: lastMessage, getAction, runAction, stopAction, addAction, updateAction, deleteAction, connected }
}



function useActionDone() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'actionDone');

    return { socket, actionDone: lastMessage }
}


/* Action types*/

function useActionGroups() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'actionGroups');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getActionGroups');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const getActionGroupsByDeviceType = (id) => {
        var actionGroups = lastMessage && lastMessage.map((element) => {
            return { ...element, actions: element.actions.filter((action) => action.deviceTypeId === id) }
        })

        return { actionGroups: actionGroups, };
    }

    return { socket, actionGroups: lastMessage, getActionGroupsByDeviceType }
}

function useActionsByBlockLibrary() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'allActionsByBlockLibrary');

    const getAllActionsByBlockLibrary = () => {
        return new Promise((resolve, reject) => {
            socket.emit("getActionsByBlockLibrary",
                (response: any) => {
                    resolve(response)
                })
        })
    }

    return { socket, actionsByBlockLibrary: lastMessage, getAllActionsByBlockLibrary, connected }
}

function useActionsGroupedByBlockLibrary() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'actionsGroupedByBlockLibrary');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getActionsGroupedByBlockLibrary');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    return { socket, actionsGroupedByBlockLibrary: lastMessage, connected }
}

// function useBlockLibraries() {
//     const { authSocket, connected, error } = useAuthenticatedSocket();
//     const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'allBlockLibraries');

//     const getAllBlockLibraries = () => {
//         return new Promise ((resolve, reject) => {
//             socket.emit("getAllBlockLibraries",
//              (response: any) => {
//                 console.log("BLOCK LIBRARIES ", response)
//                 resolve(response)
//             })
//         })
//     }
//     return { socket, blockLibraries: lastMessage, getAllBlockLibraries, connected }
// }

// function useActionGroupsByDeviceType() {
// const { authSocket, connected, error } = useAuthenticatedSocket();
// authSocket.emit('getActionGroupsByDeviceType', { deviceType: 'light' }, );
// const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'actionGroupsByDeviceType', {
//     query: 'deviceType=1'
//     });

// return { socket, actionGroups: lastMessage}
// }


/* Datasets */

function useDatasets() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'datasets');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getDatasets', {});
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const searchDatasets = (searchName: string, type?: string) => {
        if (!socket?.connected) return Promise.reject(new Error('Socket not connected'));
        return new Promise((resolve, reject) => {
            socket.emit("getDatasets", { searchName, type }, (response) => resolve(response));
        })
    }

    return { socket, datasets: lastMessage, connected, searchDatasets, }
}

function useDataset() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    // const SocketIOFileUpload = require('socketio-file-upload');
    // const uploader = new SocketIOFileUpload(authSocket);

    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'dataset');
    const { datasets } = useDatasets();

    const uploadImage = async (files, datasetId) => {
        return new Promise((resolve, reject) => {
            socket.emit('uploadImage', {
                files,
                meta: { datasetId: datasetId }
            }, (response: any) => {
                if (response.status !== 200) {
                    reject(response.message)
                }
                else {
                    resolve(response.message)
                };
            });

        })
    }

    const deleteImage = (imageId) => {
        return new Promise((resolve, reject) => {
            socket.emit("deleteImage",
                {
                    imageId,
                }, (response) => {
                    if (response.status !== 200) {
                        reject(response.message)
                    } else {
                        resolve(response.message)
                    }
                })
        })

    }

    const getDataset = (id) => {
        return new Promise((resolve, reject) => {
            socket.emit('getDataset', {
                id,
            }, (response) => {
                resolve(response);
            });
        })
    }

    // const searchDataset = (input) => {
    //     console.log("socketInput",input)
    //     return new Promise((resolve, reject) => {
    //         socket.emit("searchDataset", {
    //             input
    //         },(response) => {
    //             resolve(response)
    //         })
    //     })
    // }

    const addDataset = async (dataset) => {
        return new Promise((resolve, reject) => {
            socket.emit('addDataset', {
                dataset
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const addAnnotation = async (data) => {
        return new Promise((resolve, reject) => {
            socket.emit('addAnnotation', {
                data
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const updateDataset = (dataset) => {
        socket.emit('updateDataset', { dataset });
    }

    const deleteDataset = (id) => {

        return new Promise((resolve, reject) => {
            socket.emit('deleteDataset',
                {
                    id
                }, (response) => {
                    if (response.status !== 200) {
                        reject(response.message)
                    } else {
                        resolve(response.message)
                    }
                })
        })
    }
    
    const getDatasetMetrics = (id: number) => {
        return new Promise((resolve, reject) => {
            socket.emit('getDatasetMetrics', { id }, (response) => {
                resolve(response);
            });
        });
    }

    const addDatasetMetric = (datasetId: number, deviceId: number, metrics: any) => {
        return new Promise((resolve, reject) => {
            socket.emit('addDatasetMetric', { datasetId, deviceId, metrics }, (response) => {
                resolve(response);
            });
        });
    };

    return { socket, dataset: lastMessage, connected, getDataset, addDataset, updateDataset, deleteDataset, uploadImage, deleteImage, addAnnotation, getDatasetMetrics, addDatasetMetric };
}


/* Models */

function useModels() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'models');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getModels');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const searchModels = (searchName) => {
        if (!socket?.connected) return Promise.reject(new Error('Socket not connected'));
        return new Promise((resolve, reject) => {
            socket.emit("getModels", { searchName }, (response: any) => resolve(response));
        });
    }

    return { socket, models: lastMessage, searchModels, connected }
}

function useModel() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'model');
    const { models } = useModels();

    const trainModel = (modelId) => {
        return new Promise((resolve, reject) => {
            socket.emit("train", { modelId }, (response => {
                if (response.status !== 200) {
                    reject(response)
                } else {
                    resolve(response)
                }
            }))
        })
    }

    const stopModel = (modelId) => {
        return new Promise((resolve, reject) => {
            socket.emit("stopTrain", { modelId }, (response) => {
                if (response.status !== 200) {
                    reject(response)
                } else {
                    resolve(response)
                }
            })
        })

    }

    const getModel = (modelId) => {
        return new Promise((resolve, reject) => {
            socket.emit('getModel', {
                modelId
            }, (response) => {
                resolve(response);
            });
        })
    }
    const addModel = async (model) => {
        return new Promise((resolve, reject) => {
            socket.emit('addModel', {
                model
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const updateModel = (modelId) => {
        socket.emit('updateModel', { modelId });
    }

    const deleteModel = (modelId) => {
        return new Promise((resolve, reject) => {
            socket.emit('deleteModel',
                {
                    modelId
                }, (response) => {
                    if (response.status !== 200) {
                        reject(response)
                    } else {
                        resolve(response)
                    }
                })
        })

    }

    return { socket, model: lastMessage, connected, getModel, addModel, updateModel, deleteModel, trainModel, stopModel };

}

/* AI Methods */

function useAIMethods() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'AIMethods');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getAIMethods');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    return { socket, AIMethods: lastMessage }
}

/* Workes */

function useWorkers() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'workers');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getWorkers');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const searchWorkers = (searchName) => {
        if (!socket?.connected) return Promise.reject(new Error('Socket not connected'));
        return new Promise((resolve, reject) => {
            socket.emit("getWorkers", { searchName }, (response) => resolve(response));
        });
    }

    return { socket, workers: lastMessage, searchWorkers, connected }
}

function useWorker() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'worker');
    const { workers } = useWorkers();

    const getWorker = (id) => {
        var worker = workers && workers.find((worker: any) => worker.id == id)
        return { worker: worker };
    }
    const addWorker = async (worker) => {
        return new Promise((resolve, reject) => {
            socket.emit('addWorker', {
                worker
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const updateWorker = (worker) => {
        socket.emit('updateWorker', { worker });
    }

    const deleteWorker = (worker) => {
        socket.emit('deleteWorker', { worker });
    }

    return { socket, model: lastMessage, getWorker, addWorker, updateWorker, deleteWorker };
}

/* Block Libraries */

function useBlockLibraries() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'allBlockLibraries');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getAllBlockLibraries');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const searchBlockLibraries = (searchName) => {
        if (!socket?.connected) return Promise.reject(new Error('Socket not connected'));
        return new Promise((resolve, reject) => {
            socket.emit("getAllBlockLibraries",
                { searchName },
                (response: any) => {
                    resolve(response);
                });
        });
    }
    return { socket, blockLibraries: lastMessage, searchBlockLibraries, connected }
}

function useBlockLibrary() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'blockLibrary');

    const importBlockLibrary = (blockLibraryId) => {
        return new Promise((resolve, reject) => {
            socket.emit("importBlockLibrary", { blockLibraryId }, (response => {
                if (response.status !== 200) {
                    reject(response)
                } else {
                    resolve(response)
                }
            }))
        })
    }

    const removeBlockLibrary = (blockLibraryId) => {
        return new Promise((resolve, reject) => {
            socket.emit("removeBlockLibrary", { blockLibraryId }, (response) => {
                if (response.status !== 200) {
                    reject(response)
                } else {
                    resolve(response)
                }
            })
        })

    }

    return { socket, model: lastMessage, connected, removeBlockLibrary, importBlockLibrary };
}

function useNotifications() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'notifications');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getNotifications');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const getNotifications = () => {
        return new Promise((resolve, reject) => {
            socket.emit("getNotifications",
                (response: any) => {
                    resolve(response)
                })
        })
    }

    return { socket, notifications: lastMessage, getNotifications, connected }
}

function useNotification() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'notification');

    const readNotification = async (notificationId) => {
        return new Promise((resolve, reject) => {
            socket.emit('updateNotification', {
                notificationId
            }, (response: any) => {
                resolve(response);
            });
        })
    }

    const markAllRead = async () => {
        return new Promise((resolve, reject) => {
            socket.emit('markAllNotificationsRead', {}, (response: any) => {
                resolve(response);
            });
        })
    }

    const clearAll = async () => {
        return new Promise((resolve, reject) => {
            socket.emit('clearAllNotifications', {}, (response: any) => {
                resolve(response);
            });
        })
    }

    return { socket, notification: lastMessage, readNotification, markAllRead, clearAll };
}


function useUser() {

    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'user');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getUser');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const getUser = () => {
        return socketRequest(socket, 'getUser', {});
    }

    const updateUser = (user) => {
        socket.emit('updateUser', { user });
    }

    return { socket, user: lastMessage, getUser, updateUser, connected };
}

/* Products */

function useProducts() {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const { socket, lastMessage, sendMessage } = useSocketEvent<any>(authSocket, 'products');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (connected && authSocket && !hasFetched.current) {
            hasFetched.current = true;
            authSocket.emit('getProducts');
        }
    }, [connected, authSocket]);

    useEffect(() => {
        if (!connected) {
            hasFetched.current = false;
        }
    }, [connected]);

    const getProducts = () => {
        if (!socket?.connected) return Promise.reject(new Error('Socket not connected'));
        return new Promise((resolve, reject) => {
            socket.emit("getProducts", {}, (response: any) => resolve(response));
        });
    }

    const handleRequestQuote = (productId) => {
        return new Promise((resolve, reject) => {
            socket.emit("requestQuote", { productId }, (response => {
                if (response.status !== 200) {
                    reject(response)
                } else {
                    resolve(response)
                }
            }))
        })
    }

    return { socket, products: lastMessage, getProducts, handleRequestQuote, connected }
}

function useEmitFetchData() {
    const { authSocket, connected, error } = useAuthenticatedSocket();

    authSocket.emit('getDevices', {});
    authSocket.emit('getDeviceGroups', {});
    authSocket.emit('getActions', {})
}

/* Log Streaming */

function useLogStream(deviceId: number | null) {
    const { authSocket, connected, error } = useAuthenticatedSocket();
    const [logs, setLogs] = useState<string[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);

    useEffect(() => {
        if (!authSocket || !deviceId) return;

        const handleLogLine = (event: { data?: string; initial?: boolean; error?: string; deviceId?: number }) => {
            // Only process logs for the device we're watching
            if (event.deviceId !== deviceId) return;

            if (event.error) {
                setLogs(prev => [...prev, `[ERROR] ${event.error}`]);
                return;
            }

            if (event.data) {
                if (event.initial) {
                    // Initial batch of logs - replace current logs
                    const lines = event.data.split('\n').filter(line => line.trim());
                    setLogs(lines);
                } else {
                    // New log line - append
                    const lines = event.data.split('\n').filter(line => line.trim());
                    setLogs(prev => [...prev, ...lines]);
                }
            }
        };

        authSocket.on('logLine', handleLogLine);

        return () => {
            authSocket.off('logLine', handleLogLine);
        };
    }, [authSocket, deviceId]);

    const startStream = (lines?: number) => {
        if (!authSocket || !deviceId) return;
        setLogs([]);
        setIsStreaming(true);
        authSocket.emit('startLogStream', { deviceId, lines });
    };

    const stopStream = () => {
        if (!authSocket || !deviceId) return;
        setIsStreaming(false);
        authSocket.emit('stopLogStream', { deviceId });
    };

    return { logs, isStreaming, startStream, stopStream, connected };
}

/* Historical Log Query */

/**
 * Query a past window of a device's nexus.log.
 *
 * Results arrive as `logQueryChunk` broadcasts and are accumulated here; the whole
 * range is held in memory and the consumer is expected to render only the visible
 * slice (see Logs.tsx), since a few hours can be 15k+ lines.
 *
 * `from`/`to` are raw datetime-local strings ("2026-10-08T08:00"). They are passed
 * through untouched and interpreted in the JETSON's local timezone, which is the
 * clock its log stamps use - so no UTC conversion anywhere in the chain.
 */
function useLogQuery(deviceId: number | null) {
    const { authSocket, connected } = useAuthenticatedSocket();
    const [lines, setLines] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState<{ received: number; total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [truncated, setTruncated] = useState(false);

    // Chunks land while React state updates are still batching, so accumulate into
    // a ref and publish once per chunk rather than relying on the previous state.
    const bufferRef = useRef<string[]>([]);

    useEffect(() => {
        if (!authSocket || !deviceId) return;

        const handleChunk = (evt: any) => {
            // NotifyGroup broadcasts to the whole org, so ignore other devices.
            if (evt && evt.deviceId != null && String(evt.deviceId) !== String(deviceId)) return;

            if (evt?.done) {
                setLoading(false);
                setProgress(null);
                setTruncated(!!evt.truncated);
                if (evt.error) setError(evt.error);
                else setLines([...bufferRef.current]);
                return;
            }

            if (Array.isArray(evt?.lines)) {
                bufferRef.current = bufferRef.current.concat(evt.lines);
                // Publish incrementally so long queries fill in rather than sitting blank.
                setLines([...bufferRef.current]);
                setProgress({ received: (evt.index ?? 0) + 1, total: evt.totalChunks ?? 0 });
            }
        };

        authSocket.on('logQueryChunk', handleChunk);
        return () => {
            authSocket.off('logQueryChunk', handleChunk);
        };
    }, [authSocket, deviceId]);

    const runQuery = (from: string, to: string, filter?: string) => {
        if (!authSocket || !deviceId) return;
        bufferRef.current = [];
        setLines([]);
        setError(null);
        setTruncated(false);
        setProgress(null);
        setLoading(true);
        authSocket.emit('queryLogs', { deviceId, from, to, filter: filter || undefined });
    };

    const clear = () => {
        bufferRef.current = [];
        setLines([]);
        setError(null);
        setTruncated(false);
        setProgress(null);
        setLoading(false);
    };

    return { lines, loading, progress, error, truncated, runQuery, clear, connected };
}

/* Device Chat Agent */

export interface DeviceChatMessage {
    role: 'user' | 'assistant';
    content: string;
    /** Tool activity attached to an assistant turn (results + pending confirmations). */
    tools?: DeviceChatToolActivity[];
    /** Streaming in progress (assistant only). */
    streaming?: boolean;
}

export interface DeviceChatToolActivity {
    toolUseId?: string;
    name: string;
    input?: any;
    summary?: string;
    /** 'confirm' = awaiting approval, 'ran' = executed ok, 'failed' = executed with error, 'denied' = user denied. */
    state: 'confirm' | 'ran' | 'failed' | 'denied';
}

/**
 * Per-device chat agent hook. Talks to Hive's deviceChat:* socket events, streams the
 * assistant reply token-by-token, and surfaces control-tool confirmations for the UI to
 * approve/deny. Session is keyed server-side by (socket, deviceId); resets on device change.
 */
function useDeviceChat(deviceId: number) {
    const { authSocket, connected } = useAuthenticatedSocket();
    const [messages, setMessages] = useState<DeviceChatMessage[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Pending control-tool confirmations for the current turn. */
    const [pendingConfirms, setPendingConfirms] = useState<DeviceChatToolActivity[]>([]);

    const sessionMatches = (sid: string) =>
        typeof sid === 'string' && sid.endsWith(`:${deviceId}`);

    useEffect(() => {
        if (!authSocket) return;

        const onText = (e: any) => {
            if (!sessionMatches(e?.sessionId)) return;
            setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.role === 'assistant' && last.streaming) {
                    next[next.length - 1] = { ...last, content: last.content + (e.text || '') };
                } else {
                    next.push({ role: 'assistant', content: e.text || '', streaming: true });
                }
                return next;
            });
        };

        const onToolConfirm = (e: any) => {
            if (!sessionMatches(e?.sessionId)) return;
            const activity: DeviceChatToolActivity = {
                toolUseId: e.toolUseId, name: e.name, input: e.input, summary: e.summary, state: 'confirm',
            };
            setPendingConfirms((prev) => [...prev, activity]);
        };

        const onToolResult = (e: any) => {
            if (!sessionMatches(e?.sessionId)) return;
            setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1] || { role: 'assistant', content: '', tools: [] };
                const tools = [...(last.tools || []), { name: e.name, state: e.ok ? 'ran' : 'failed' } as DeviceChatToolActivity];
                if (last.role === 'assistant') {
                    next[next.length - 1] = { ...last, tools };
                } else {
                    next.push({ role: 'assistant', content: '', tools, streaming: true });
                }
                return next;
            });
        };

        const onDone = (e: any) => {
            if (!sessionMatches(e?.sessionId)) return;
            setBusy(false);
            setMessages((prev) => prev.map((m, i) => (i === prev.length - 1 && m.role === 'assistant' ? { ...m, streaming: false } : m)));
        };

        const onError = (e: any) => {
            if (!sessionMatches(e?.sessionId)) return;
            setBusy(false);
            setError(e?.error || 'Chat error');
        };

        authSocket.on('deviceChat:text', onText);
        authSocket.on('deviceChat:toolConfirm', onToolConfirm);
        authSocket.on('deviceChat:toolResult', onToolResult);
        authSocket.on('deviceChat:done', onDone);
        authSocket.on('deviceChat:error', onError);

        return () => {
            authSocket.off('deviceChat:text', onText);
            authSocket.off('deviceChat:toolConfirm', onToolConfirm);
            authSocket.off('deviceChat:toolResult', onToolResult);
            authSocket.off('deviceChat:done', onDone);
            authSocket.off('deviceChat:error', onError);
        };
    }, [authSocket, deviceId]);

    const sendMessage = (text: string) => {
        if (!authSocket || !text.trim()) return;
        setError(null);
        setBusy(true);
        setMessages((prev) => [...prev, { role: 'user', content: text }]);
        authSocket.emit('deviceChat:send', { deviceId, message: text });
    };

    /** Approve/deny the pending control tools, then resume the model. */
    const resolveConfirms = (decisions: Record<string, boolean>) => {
        if (!authSocket) return;
        // Reflect the decision in the transcript before clearing.
        setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            const decided = pendingConfirms.map((c) => ({
                ...c, state: decisions[c.toolUseId as string] ? 'ran' : 'denied' as any,
            }));
            if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, tools: [...(last.tools || []), ...decided] };
            } else {
                next.push({ role: 'assistant', content: '', tools: decided, streaming: true });
            }
            return next;
        });
        setPendingConfirms([]);
        setBusy(true);
        authSocket.emit('deviceChat:confirm', { deviceId, decisions });
    };

    const reset = () => {
        if (authSocket) authSocket.emit('deviceChat:reset', { deviceId });
        setMessages([]);
        setPendingConfirms([]);
        setError(null);
        setBusy(false);
    };

    return { messages, sendMessage, busy, error, pendingConfirms, resolveConfirms, reset, connected };
}

export {
    useAuthenticatedSocket,
    useDevices,
    useDevice,
    useDeviceGroups,
    useDeviceGroup,
    useActions,
    useAction,
    useActionDone,
    useActionGroups,
    usePrograms,
    useProgram,
    useProgramDone,
    useActiveBlock,
    useDatasets,
    useDataset,
    useModels,
    useModel,
    useWorkers,
    useWorker,
    useAIMethods,
    useEmitFetchData,
    useDeviceTypes,
    useDeviceType,
    useBlockLibraries,
    useBlockLibrary,
    useUser,
    useMetrics,
    useActionsByBlockLibrary,
    useActionsGroupedByBlockLibrary,
    useNotifications,
    useNotification,
    useProducts,
    useLogStream,
    useLogQuery,
    useDeviceChat
}